import JobsList from '@/components/features/WorkOrders/components/JobsList';
import { WorkOrderCard } from '@/components/features/WorkOrders/components/WorkOrderCard';
import { CancelWorkOrderDialog } from '@/components/features/WorkOrders/modals/CancelWorkOrderDialog';
import { CompleteWorkOrderDialog } from '@/components/features/WorkOrders/modals/CompleteWorkOrderDialog';
import { WorkOrderTable } from '@/components/features/WorkOrders/tables/WorkOrderTable';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters, type FilterSpecMap } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type Company, type PageProps, type PaginationMeta, type User, type WorkOrder } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import { Calendar } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface WorkOrdersPageProps extends PageProps {
    workOrders: {
        data: WorkOrder[];
        // Resource collections may provide pagination in `meta`
        meta?: PaginationMeta;
        current_page?: number;
        last_page?: number;
        per_page?: number;
        total?: number;
        from?: number | null;
        to?: number | null;
    };
    companies: Company[];
    users: User[];
    statuses: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities: Array<{ value: string; label: string; color: string; icon: string }>;
    types: Array<{ value: string; label: string; color: string; icon: string }>;
    availableFilterValues?: {
        status: string[];
        priority: string[];
        type: string[];
        company_id: string[];
        assigned_user_id: string[];
        created_by_user_id: string[];
    };
    filters: {
        status?: string | string[];
        company_id?: string | string[];
        assigned_user_id?: string | string[];
        created_by_user_id?: string | string[];
        search?: string;
        unscheduled?: boolean;
        overdue?: boolean;
        priority?: string | string[];
        work_type?: string | string[];
        estimated_date_from?: string;
        estimated_date_to?: string;
        per_page?: number;
        page?: number;
        sort_column?: string;
        sort_direction?: string;
    };
    auth: {
        user: User & { name: string };
    };
    [key: string]: unknown;
}

export default function WorkOrdersIndex() {
    const { props } = usePage<WorkOrdersPageProps>();
    const { workOrders: workOrdersData, companies, users, statuses, priorities, types, availableFilterValues, filters = {} } = props;
    const { t } = useTranslations();

    const breadcrumbs = useBreadcrumbs('work-orders');

    const columnDefinitions = [
        { id: 'work_order', label: t('work_orders.index.columns.work_order', 'Work Order') },
        { id: 'contact', label: t('work_orders.index.columns.contact', 'Contact') },
        { id: 'service_address', label: t('work_orders.index.columns.service_address', 'Service Address') },
        { id: 'company', label: t('work_orders.index.columns.company', 'Company') },
        { id: 'assigned_to', label: t('work_orders.index.columns.assigned_to', 'Assigned To') },
        { id: 'type', label: t('work_orders.index.columns.type', 'Type') },
        { id: 'status', label: t('work_orders.index.columns.status', 'Status') },
        { id: 'priority', label: t('work_orders.index.columns.priority', 'Priority') },
        { id: 'estimated_date', label: t('work_orders.index.columns.estimated_date', 'Estimated Date') },
        { id: 'created_at', label: t('work_orders.index.columns.created_at', 'Created At') },
        { id: 'created_by', label: t('work_orders.index.columns.created_by', 'Created By') },
    ];

    const quickFilterDefinitions = [
        { id: 'status', label: t('work_orders.index.quick_filters.status', 'Status') },
        { id: 'type', label: t('work_orders.index.quick_filters.type', 'Type') },
        { id: 'priority', label: t('work_orders.index.quick_filters.priority', 'Priority') },
        { id: 'overdue_or_unscheduled', label: t('work_orders.index.quick_filters.overdue_or_unscheduled', 'Overdue/Unscheduled') },
    ];

    // Server-sortable subset (matches WorkOrderController@index whitelist)
    const sortableColumns = [
        { id: 'work_order', label: t('work_orders.index.columns.work_order', 'Work Order') },
        { id: 'contact', label: t('work_orders.index.columns.contact', 'Contact') },
        { id: 'status', label: t('work_orders.index.columns.status', 'Status') },
        { id: 'priority', label: t('work_orders.index.columns.priority', 'Priority') },
        { id: 'estimated_date', label: t('work_orders.index.columns.estimated_date', 'Estimated Date') },
    ];

    // Role-based permissions
    const currentUser = props.auth.user;
    const userRole = currentUser?.role;
    const isServiceWorker = userRole === 'Service Worker';
    const isServiceManagerLike = userRole === 'Administrator' || userRole === 'Service Manager' || userRole === 'Service Supervisor';
    const canInlineEditTable = isServiceManagerLike;

    const isMobile = useIsMobile();
    // Service Worker is hard-locked to Jobs view
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isServiceWorker ? 'jobs' : isMobile ? 'grid' : 'table');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [searchValue, setSearchValue] = useState(filters.search ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [estimatedDateFrom, setEstimatedDateFrom] = useState<string>(filters.estimated_date_from ?? '');
    const [estimatedDateTo, setEstimatedDateTo] = useState<string>(filters.estimated_date_to ?? '');
    const [sortColumn, setSortColumn] = useState<string | null>(filters.sort_column ?? null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
        filters.sort_direction === 'asc' || filters.sort_direction === 'desc' ? filters.sort_direction : null,
    );
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
        new Set(['work_order', 'contact', 'service_address', 'company', 'assigned_to', 'type', 'status', 'priority', 'estimated_date', 'created_at']),
    );
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [completeDialogWorkOrder, setCompleteDialogWorkOrder] = useState<WorkOrder | null>(null);
    const [cancelDialogWorkOrder, setCancelDialogWorkOrder] = useState<WorkOrder | null>(null);

    /**
     * Column id -> query parameter. Every filter here is applied by the server across
     * the whole table, so the results are never limited to the rows on the current page.
     */
    const filterSpecs = useMemo<FilterSpecMap>(
        () => ({
            status: { param: 'status' },
            priority: { param: 'priority' },
            type: { param: 'work_type' },
            company: { param: 'company_id' },
            assigned_to: { param: 'assigned_user_id' },
            created_by: { param: 'created_by_user_id' },
            // One UI control, two backend flags — the server ORs them together.
            overdue_or_unscheduled: {
                param: 'overdue_or_unscheduled',
                toQuery: () => undefined,
                extraParams: (values) => ({
                    overdue: values.includes('overdue') ? true : undefined,
                    unscheduled: values.includes('unscheduled') ? true : undefined,
                }),
                fromQuery: (serverFilters) => {
                    const selected: string[] = [];
                    if (serverFilters.overdue) selected.push('overdue');
                    if (serverFilters.unscheduled) selected.push('unscheduled');
                    return selected;
                },
            },
        }),
        [],
    );

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/work-orders');

    // Translate statuses, priorities, and types (same logic as WorkOrderTable)
    const translatedStatuses = useMemo(() => {
        return statuses?.map((status) => ({
            ...status,
            label: t(`settings.work_order_statuses.${status.value}`, status.label),
        }));
    }, [statuses, t]);

    const translatedPriorities = useMemo(() => {
        return priorities?.map((priority) => ({
            ...priority,
            label: t(`settings.work_order_priorities.${priority.value}`, priority.label),
        }));
    }, [priorities, t]);

    const translatedTypes = useMemo(() => {
        return types?.map((type) => {
            const translationKeyValue = type.value.trim().toLowerCase().replace(/\s+/g, '_');
            return {
                ...type,
                label: t(`settings.work_order_types.${translationKeyValue}`, type.label),
            };
        });
    }, [types, t]);

    const pagination = useMemo<PaginationMeta>(() => {
        const meta = workOrdersData.meta;
        if (meta) {
            return meta;
        }

        return {
            current_page: workOrdersData.current_page ?? 1,
            last_page: workOrdersData.last_page ?? 1,
            per_page: workOrdersData.per_page ?? 10,
            total: workOrdersData.total ?? workOrdersData.data.length,
            from: workOrdersData.from ?? null,
            to: workOrdersData.to ?? null,
        };
    }, [workOrdersData]);

    useEffect(() => {
        // Service Worker is always locked to Jobs view
        if (isServiceWorker) {
            setViewMode('jobs');
            return;
        }

        setViewMode((current) => {
            if (current === 'jobs') {
                return current;
            }

            return isMobile ? 'grid' : 'table';
        });
    }, [isMobile, isServiceWorker]);

    useEffect(() => {
        const nextSearch = filters.search ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    /**
     * Filter options come from `availableFilterValues` — distinct values across the whole
     * table — rather than from the rows on screen, so the dropdowns offer every value that
     * exists, not just the ones this page happens to show.
     *
     * Free-text columns (work order number, contact, service address, estimated date) are
     * deliberately absent: they are effectively unique per row, so a checkbox list of them
     * is useless. The search box covers them, server-side.
     */
    const { columnFilterOptions } = useMemo(() => {
        const optionsRecord: Record<string, Array<{ value: string; label: string }>> = {};

        const availableStatusValues = new Set(availableFilterValues?.status ?? []);
        const availablePriorityValues = new Set(availableFilterValues?.priority ?? []);
        const availableTypeValues = new Set(availableFilterValues?.type ?? []);
        const availableCompanyIds = new Set(availableFilterValues?.company_id ?? []);
        const availableAssignedUserIds = new Set(availableFilterValues?.assigned_user_id ?? []);
        const availableCreatedByUserIds = new Set(availableFilterValues?.created_by_user_id ?? []);
        const statusOptions = availableStatusValues.size ? translatedStatuses.filter((s) => availableStatusValues.has(s.value)) : translatedStatuses;
        const priorityOptions = availablePriorityValues.size
            ? translatedPriorities.filter((p) => availablePriorityValues.has(p.value))
            : translatedPriorities;
        const typeOptions = availableTypeValues.size ? translatedTypes.filter((t) => availableTypeValues.has(t.value)) : translatedTypes;
        const companyOptions = availableCompanyIds.size ? companies.filter((company) => availableCompanyIds.has(company.id)) : companies;
        const assignedUserOptions = availableAssignedUserIds.size ? users.filter((user) => availableAssignedUserIds.has(user.id)) : users;
        const createdByUserOptions = availableCreatedByUserIds.size ? users.filter((user) => availableCreatedByUserIds.has(user.id)) : users;
        optionsRecord.status = statusOptions.map((s) => ({ value: s.value, label: s.label }));
        optionsRecord.priority = priorityOptions.map((p) => ({ value: p.value, label: p.label }));
        optionsRecord.type = typeOptions.map((t) => ({ value: t.value, label: t.label }));
        optionsRecord.company = companyOptions.map((company) => ({ value: company.id, label: company.name }));
        optionsRecord.assigned_to = assignedUserOptions.map((user) => ({
            value: user.id,
            label: `${user.first_name} ${user.last_name}`.trim(),
        }));
        optionsRecord.created_by = createdByUserOptions.map((user) => ({
            value: user.id,
            label: `${user.first_name} ${user.last_name}`.trim(),
        }));

        // Add filter options for overdue/unscheduled
        optionsRecord.overdue_or_unscheduled = [
            { value: 'overdue', label: t('work_orders.index.filters.overdue', 'Overdue') },
            { value: 'unscheduled', label: t('work_orders.index.filters.unscheduled', 'Unscheduled') },
        ];

        return { columnFilterOptions: optionsRecord };
    }, [companies, users, translatedStatuses, translatedPriorities, translatedTypes, availableFilterValues, t]);

    const quickFilterNames = quickFilterDefinitions.filter((q) => (columnFilters[q.id] || []).length > 0).map((q) => q.label);
    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('work_orders.index.toolbar.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('work_orders.index.filters.all_work_orders', 'All Work Orders');

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        applyFilters({ sort_column: column, sort_direction: direction, page: 1 } as Record<string, string | number | undefined>);
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_column: undefined, sort_direction: undefined, page: 1 } as Record<string, string | number | undefined>);
    };

    const handleEstimatedDateFrom = (value: string) => {
        setEstimatedDateFrom(value);
        applyFilters({ estimated_date_from: value || undefined, page: 1 } as Record<string, string | number | undefined>);
    };

    const handleEstimatedDateTo = (value: string) => {
        setEstimatedDateTo(value);
        applyFilters({ estimated_date_to: value || undefined, page: 1 } as Record<string, string | number | undefined>);
    };

    const resetEstimatedDates = () => {
        setEstimatedDateFrom('');
        setEstimatedDateTo('');
        applyFilters({ estimated_date_from: undefined, estimated_date_to: undefined, page: 1 } as Record<string, string | number | undefined>);
    };

    const clearFilters = () => {
        setSearchValue('');
        setEstimatedDateFrom('');
        setEstimatedDateTo('');
        // The spec map covers the checkbox filters; the date range is page-local, so it
        // has to be cleared explicitly.
        clearServerFilters({ estimated_date_from: undefined, estimated_date_to: undefined });
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.delete(column);
            return next;
        });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((prev) => {
            const next = new Set(prev);
            next.add(column);
            return next;
        });
    };

    const handleUpdate = {
        estimatedDate: async (workOrderId: string, value: string | null) => {
            const workOrder = workOrdersData.data.find((wo) => wo.id === workOrderId);
            if (!workOrder) return;

            router.patch(
                `/work-orders/${workOrderId}`,
                {
                    estimated_date: value || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('work_orders.index.messages.estimated_date_updated', 'Estimated date updated'));
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.failed_update_estimated_date', 'Failed to update estimated date'));
                    },
                },
            );
        },
        priority: async (workOrderId: string, value: string) => {
            router.patch(
                `/work-orders/${workOrderId}/priority`,
                {
                    priority: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('work_orders.index.messages.priority_updated', 'Priority updated'));
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.failed_update_priority', 'Failed to update priority'));
                    },
                },
            );
        },
        status: async (workOrderId: string, value: string) => {
            const workOrder = workOrdersData.data.find((wo) => wo.id === workOrderId);
            if (!workOrder) {
                throw new Error('Work order not found');
            }

            // Find the status configuration from the statuses array by value
            const statusConfig = statuses.find((s) => s.value === value);

            // Check if this is a completed or cancelled status
            const isCompleted = statusConfig?.value === 'completed' || (!statusConfig && value === 'completed');
            const isCancelled =
                statusConfig?.value === 'cancelled' ||
                statusConfig?.value === 'canceled' ||
                (!statusConfig && (value === 'cancelled' || value === 'canceled'));

            if (isCompleted) {
                setCompleteDialogWorkOrder(workOrder);
                // Return a resolved promise so the UI doesn't hang
                return Promise.resolve();
            }

            if (isCancelled) {
                setCancelDialogWorkOrder(workOrder);
                // Return a resolved promise so the UI doesn't hang
                return Promise.resolve();
            }

            // For all other statuses, update immediately
            return new Promise<void>((resolve, reject) => {
                router.patch(
                    `/work-orders/${workOrderId}/status-editable`,
                    {
                        status: value,
                    },
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onSuccess: () => {
                            toast.success(t('work_orders.index.messages.status_updated', 'Status updated'));
                            resolve();
                        },
                        onError: (errors) => {
                            const errorMessage =
                                typeof errors === 'object' && errors !== null && 'message' in errors
                                    ? String(errors.message)
                                    : t('work_orders.index.messages.failed_update_status', 'Failed to update status');
                            toast.error(errorMessage);
                            reject(new Error(errorMessage));
                        },
                    },
                );
            });
        },
        type: async (workOrderId: string, value: string) => {
            const workOrder = workOrdersData.data.find((wo) => wo.id === workOrderId);
            if (!workOrder) return;

            router.patch(
                `/work-orders/${workOrderId}/details`,
                {
                    work_type: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('work_orders.index.messages.type_updated', 'Type updated'));
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.failed_update_type', 'Failed to update type'));
                    },
                },
            );
        },
        address: async (workOrderId: string, value: string, addressData?: { lat?: number; lng?: number; place_id?: string }) => {
            const workOrder = workOrdersData.data.find((wo) => wo.id === workOrderId);
            if (!workOrder) return;

            router.patch(
                `/work-orders/${workOrderId}`,
                {
                    service_address_street: value,
                    service_address_lat: addressData?.lat,
                    service_address_lng: addressData?.lng,
                    service_address_place_id: addressData?.place_id,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('work_orders.index.messages.address_updated', 'Address updated'));
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.failed_update_address', 'Failed to update address'));
                    },
                },
            );
        },
        contact: async (
            workOrderId: string,
            data: {
                contact_first_name: string;
                contact_last_name: string;
                phone?: string;
                email?: string;
                service_address: string;
                service_address_lat?: number;
                service_address_lng?: number;
                service_address_street?: string;
                service_address_house_number?: string;
                service_address_city?: string;
                service_address_postal_code?: string;
                service_address_place_id?: string;
            },
        ) => {
            router.patch(
                `/work-orders/${workOrderId}/details`,
                {
                    contact_first_name: data.contact_first_name,
                    contact_last_name: data.contact_last_name,
                    phone: data.phone,
                    email: data.email,
                    service_address_street: data.service_address_street,
                    service_address_house_number: data.service_address_house_number,
                    service_address_city: data.service_address_city,
                    service_address_postal_code: data.service_address_postal_code,
                    service_address_lat: data.service_address_lat,
                    service_address_lng: data.service_address_lng,
                    service_address_place_id: data.service_address_place_id,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('work_orders.index.messages.contact_updated', 'Contact updated'));
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.failed_update_contact', 'Failed to update contact'));
                    },
                },
            );
        },
        assignedUser: async (workOrderId: string, userId: string | null) => {
            router.patch(
                `/work-orders/${workOrderId}/assign`,
                {
                    assigned_user_id: userId || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(
                            userId
                                ? t('work_orders.index.messages.user_assigned', 'User assigned')
                                : t('work_orders.index.messages.user_unassigned', 'User unassigned'),
                        );
                    },
                    onError: () => {
                        toast.error(t('work_orders.index.messages.assignment_failed', 'Failed to update assignment'));
                    },
                },
            );
        },
    };

    const handleComplete = (data: { hours: number; minutes: number; notes?: string }) => {
        if (!completeDialogWorkOrder) return;

        router.post(
            `/work-orders/${completeDialogWorkOrder.id}/complete`,
            {
                hours: data.hours,
                minutes: data.minutes,
                notes: data.notes,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setCompleteDialogWorkOrder(null);
                    toast.success(t('work_orders.index.messages.work_order_completed', 'Work order completed'));
                    // Reload work orders to get updated data
                    router.reload({ only: ['workOrders'] });
                },
                onError: (errors) => {
                    const errorMessage =
                        typeof errors === 'object' && errors !== null && 'message' in errors
                            ? String(errors.message)
                            : t('work_orders.index.messages.failed_complete', 'Failed to complete work order');
                    toast.error(errorMessage);
                },
            },
        );
    };

    const handleCancel = (reason: string) => {
        if (!cancelDialogWorkOrder) return;

        // Find the correct cancelled status value from statuses array
        const cancelledStatus = statuses.find((s) => s.value === 'cancelled' || s.value === 'canceled')?.value || 'cancelled';

        router.patch(
            `/work-orders/${cancelDialogWorkOrder.id}/status-editable`,
            {
                status: cancelledStatus,
                cancellation_reason: reason,
            },
            {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    setCancelDialogWorkOrder(null);
                    toast.success(t('work_orders.index.messages.work_order_cancelled', 'Work order cancelled'));
                    // Reload work orders to get updated data
                    router.reload({ only: ['workOrders'] });
                },
                onError: (errors) => {
                    console.error('Cancel work order error:', errors);
                    let errorMessage = t('work_orders.index.messages.failed_cancel', 'Failed to cancel work order');

                    if (typeof errors === 'object' && errors !== null) {
                        if ('message' in errors) {
                            errorMessage = String(errors.message);
                        } else if ('error' in errors) {
                            errorMessage = String(errors.error);
                        } else if (Array.isArray(errors)) {
                            errorMessage = errors.join(', ');
                        } else {
                            // Try to extract error from validation errors
                            const errorValues = Object.values(errors).flat();
                            if (errorValues.length > 0) {
                                errorMessage = errorValues.join(', ');
                            }
                        }
                    }

                    toast.error(errorMessage);
                },
            },
        );
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('work_orders.index.title', 'Work Orders')} />
            <PageHeader
                title={t('work_orders.index.heading', 'Work Orders')}
                subtitle={t('work_orders.index.subheading', 'Manage service requests and track work progress')}
                actions={
                    !isServiceWorker && (
                        <CreateButton
                            href="/work-orders/create"
                            permission="work-orders.create"
                            label={t('work_orders.index.actions.create', 'Create Work Order')}
                        />
                    )
                }
            />

            <PageContent>
                {/* Toolbar - hidden for Service Worker */}
                {!isServiceWorker && (
                    <GenericToolbar
                        quickFilterButtonLabel={quickFilterButtonLabel}
                        quickFilterDefinitions={quickFilterDefinitions}
                        columnDefinitions={columnDefinitions}
                        columnFilterOptions={columnFilterOptions}
                        columnFilters={columnFilters}
                        columnFilterSearch={columnFilterSearch}
                        setColumnFilterSearch={setColumnFilterSearch}
                        toggleSingleFilter={toggleSingleFilter}
                        handleFilterChange={handleFilterChange}
                        clearFilters={clearFilters}
                        isFilterOpen={isFilterOpen}
                        setIsFilterOpen={setIsFilterOpen}
                        isSortOpen={isSortOpen}
                        setIsSortOpen={setIsSortOpen}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        handleSortChange={handleSortChange}
                        clearSort={clearSort}
                        search={searchValue}
                        setSearch={setSearchValue}
                        perPage={pagination.per_page}
                        onPerPageChange={handlePerPageChange}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        visibleColumns={visibleColumns}
                        handleColumnShow={handleColumnShow}
                        handleColumnHide={handleColumnHide}
                        filteredCount={pagination.total}
                        resourceName="work_orders"
                        resourceNamePlural={t('work_orders.index.resource_plural', 'work orders')}
                        sortableColumns={sortableColumns}
                        excludeFromMoreFilters={['created_at']}
                        customFilters={
                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-sm font-medium">
                                    <span>{t('work_orders.index.filters.estimated_date_range', 'Estimated date')}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                        disabled={!estimatedDateFrom && !estimatedDateTo}
                                        onClick={resetEstimatedDates}
                                    >
                                        {t('common.reset', 'Reset')}
                                    </Button>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    <div className="grid gap-1">
                                        <label className="text-muted-foreground text-xs">
                                            {t('work_orders.index.filters.from_date', 'From date')}
                                        </label>
                                        <DatePicker
                                            value={estimatedDateFrom}
                                            onValueChange={handleEstimatedDateFrom}
                                            placeholder={t('common.pick_date', 'Pick a date')}
                                        />
                                    </div>
                                    <div className="grid gap-1">
                                        <label className="text-muted-foreground text-xs">{t('work_orders.index.filters.to_date', 'To date')}</label>
                                        <DatePicker
                                            value={estimatedDateTo}
                                            onValueChange={handleEstimatedDateTo}
                                            placeholder={t('common.pick_date', 'Pick a date')}
                                        />
                                    </div>
                                </div>
                            </div>
                        }
                        extraViewMode={{
                            value: 'jobs',
                            icon: <Calendar className="h-4 w-4" />,
                            label: t('work_orders.index.toolbar.view_mode.jobs', 'Jobs'),
                        }}
                    />
                )}

                {/* Content */}
                <GoogleMapsProvider>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {workOrdersData.data.map((workOrder) => (
                                <WorkOrderCard
                                    key={workOrder.id}
                                    workOrder={workOrder}
                                    statuses={translatedStatuses}
                                    priorities={translatedPriorities}
                                    types={translatedTypes}
                                />
                            ))}
                        </div>
                    ) : viewMode === 'table' ? (
                        <WorkOrderTable
                            workOrders={workOrdersData.data}
                            users={users}
                            onUpdate={canInlineEditTable ? handleUpdate : undefined}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                            onColumnHide={handleColumnHide}
                            onColumnShow={handleColumnShow}
                            visibleColumns={visibleColumns}
                            columnFilters={columnFilters}
                            columnFilterOptions={columnFilterOptions}
                            onFilterChange={handleFilterChange}
                            statuses={translatedStatuses}
                            priorities={translatedPriorities}
                            types={translatedTypes}
                            readOnly={!canInlineEditTable}
                        />
                    ) : (
                        <div className="mx-auto flex w-full min-w-0 flex-col gap-4 overflow-x-hidden md:max-w-screen-sm">
                            <JobsList
                                workOrders={workOrdersData.data}
                                selectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                auth={props.auth}
                                statuses={translatedStatuses}
                                priorities={translatedPriorities}
                                types={translatedTypes}
                            />
                        </div>
                    )}
                </GoogleMapsProvider>

                {viewMode !== 'jobs' && (
                    <PaginationControls
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onPerPageChange={handlePerPageChange}
                        loading={loading}
                        className="mt-6"
                    />
                )}

                {viewMode === 'grid' && workOrdersData.data.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">
                                {t('work_orders.index.empty.no_work_orders_found', 'No work orders found matching your filters.')}
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Complete Work Order Dialog */}
                {completeDialogWorkOrder && (
                    <CompleteWorkOrderDialog
                        workOrder={completeDialogWorkOrder}
                        isOpen={!!completeDialogWorkOrder}
                        onClose={() => setCompleteDialogWorkOrder(null)}
                        onComplete={handleComplete}
                    />
                )}

                {/* Cancel Work Order Dialog */}
                {cancelDialogWorkOrder && (
                    <CancelWorkOrderDialog
                        workOrder={cancelDialogWorkOrder}
                        isOpen={!!cancelDialogWorkOrder}
                        onClose={() => setCancelDialogWorkOrder(null)}
                        onCancel={handleCancel}
                    />
                )}
            </PageContent>
        </AppLayout>
    );
}
